import React, { useState, useContext, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { withRouter } from "../../../providers/withRouter";
import { DataTable } from "../../../components";
import { Box, Tooltip, IconButton } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { getChildrenAaffs } from "../../../services/aaffs.service";
import { loginAsAAFF } from "../../../services/auth.service";
import BreadCrumb from "../../../components/navbar/Breadcrumb";
import { UsuarioContext } from "../../../providers/userContext";
import FiltrosOficinas from "../../../components/filtros/filtrosOficinas";
import { useNavigate } from "react-router-dom";
import { useRecogerURLParamsFiltros, construirURLParamsFiltros } from './../../../functions/parametrosURL/funcionesParamsURL';
import { Login } from "@mui/icons-material";

const buildHierarchy = (flatData) => {
  const map = {};
  const roots = [];

  flatData.forEach(item => {
    map[item.id] = { ...item, children: [] };
  });

  flatData.forEach(item => {
    if (item.parent_id && map[item.parent_id]) {
      map[item.parent_id].children.push(map[item.id]);
    } else {
      roots.push(map[item.id]);
    }
  });

  return roots;
};

const flattenHierarchy = (hierarchy, expandedRows) => {
  const result = [];

  const flatten = (items) => {
    items.forEach(item => {
      result.push(item);
      if (item.children && item.children.length > 0 && expandedRows[item.id]) {
        flatten(item.children);
      }
    });
  };

  flatten(hierarchy);
  return result;
};

const Oficinas = () => {
  const { t } = useTranslation(['aaff', 'common']);

  const oficinasRows = [
    {
      id: "razonSocial",
      numeric: false,
      disablePadding: false,
      label: t('fields.companyName'),
      overrideFunc: (data, row, expandedRows, toggleRow) => {
        const hasChildren = row.children && row.children.length > 0;
        const isExpanded = expandedRows[row.id];
        const nivel = row.nivel || 1;

        return (
          <div style={{
            paddingLeft: `${(nivel - 1) * 30}px`,
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleRow(row.id);
                }}
                style={{ padding: '4px' }}
              >
                {isExpanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
              </IconButton>
            ) : (
              <span style={{ width: '28px', display: 'inline-block' }}></span>
            )}
            <span>{data}</span>
          </div>
        );
      }
    },
    {
      id: "provincia",
      numeric: false,
      disablePadding: false,
      label: t('common:form.province').toUpperCase(),
    },
    {
      id: "login",
      numeric: false,
      disablePadding: false,
      label: "",
      noObjeto: true,
      overrideFunc: (data, row) => (
        <Tooltip title={t('common:messages.loginAs', { name: row.razonSocial })}>
          <div className="bigBtn p-2" onClick={() => loginAsAAFF(row.nif)}>
            <Login fontSize="small" />
          </div>
        </Tooltip>
      )
    },
  ];
const { filtrosIniciales, filtroOrder } = useRecogerURLParamsFiltros();
  const [busqueda, setBusqueda] = useState(filtrosIniciales);
  const [loading, setLoading] = useState(true);
  const [busquedaActiva, setBusquedaActiva] = useState(false);
  const [clearFilterActivo, setClearFilterActivo] = useState(false);
  const [data, setData] = useState({
    meta: {},
    rows: [],
  });
  const [expandedRows, setExpandedRows] = useState({});
  const hasInitialFetch = useRef(false);

  const navigate = useNavigate();
  const user = useContext(UsuarioContext);

  console.log("User context in Oficinas:", user);

  const clearFilters = useCallback(() => {
    setBusqueda({});
    setClearFilterActivo(true);
  }, []);

  const toggleRow = useCallback((id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  }, []);

  const fetchData = async ({ offset, limit, filtros, order, orderBy } = {}) => {
    if (!user || !user.id) {
      console.error("User ID is not available");
      setLoading(false);
      return;
    }

    const finalFiltros = filtros !== undefined ? filtros : busqueda;

    orderBy = orderBy || filtroOrder.ordenadoPor || 'razonSocial';
    order = order || filtroOrder.orden || 'ASC';

    construirURLParamsFiltros(navigate, offset || 1, limit || 50, finalFiltros, order, orderBy);

    const payload = {
      filtros: finalFiltros,
      params: { page: 1, limit: 10000, order, orderBy },
    };

    try {
      setLoading(true);
      const response = await getChildrenAaffs(user.id, payload);
      const { meta, data: oficinasData } = response;

      const formattedData = formatData(oficinasData);

      setData({
        meta,
        rows: formattedData,
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const formatData = (data) => {
    return data.map((row) => ({
      razonSocial: row.razonSocial,
      nif: row.nif,
      id: row.id,
      parent_id: row.parent_id,
      provincia: row.provincia || "-",
      nivel: row.nivel || 1
    }));
  };

  const filtrarDatos = useCallback((filtros) => {
    setBusqueda(filtros);
    setBusquedaActiva(true);
  }, []);

  useEffect(() => {
    if (user && user.id && !hasInitialFetch.current) {
      hasInitialFetch.current = true;
      fetchData();
    }
  }, [user?.id]);

  useEffect(() => {
    if (busquedaActiva) {
      fetchData({ filtros: busqueda });
      setBusquedaActiva(false);
    }
  }, [busquedaActiva]);

  useEffect(() => {
    if (clearFilterActivo) {
      fetchData({ filtros: {} });
      setClearFilterActivo(false);
    }
  }, [clearFilterActivo]);

  const hierarchy = useMemo(() => buildHierarchy(data.rows), [data.rows]);
  const displayRows = useMemo(() => flattenHierarchy(hierarchy, expandedRows), [hierarchy, expandedRows]);

  const enrichedHeaders = useMemo(() =>
    oficinasRows.map(header => ({
      ...header,
      overrideFunc: header.overrideFunc
        ? (data, row) => header.overrideFunc(data, row, expandedRows, toggleRow)
        : undefined
    })),
    [expandedRows, toggleRow]
  );

  return (
    <>
      <BreadCrumb rutas={[{ path: `/oficinas`, label: t('fields.office') }]} />
      <div className="listPage">
        <div className="gridCard">
          <div className="gridHead">{t('officesList').toUpperCase()}</div>
          <div className="tableContainer">
            <div>
              <FiltrosOficinas
                onFilter={filtrarDatos}
                clearFilter={clearFilters}
                filtrosIniciales={busqueda}
              />
            </div>
            <div className="tableWrapper" style={{ marginTop: '50px' }}>
              <Box className="pageContent">
                <Box sx={{ mb: 10 }}>
                  <DataTable
                    data={displayRows}
                    isLoading={loading}
                    headers={enrichedHeaders}
                    fetcher={fetchData}
                    currentPage={1}
                    meta={{
                      total: displayRows.length,
                      last_page: displayRows.length > 0 ? 1 : 0,
                      per_page: displayRows.length
                    }}
                    totalItems={displayRows.length}
                    totalPages={displayRows.length > 0 ? 1 : 0}
                    nextPage={null}
                    filtros={busqueda}
                    busquedaActiva={busquedaActiva}
                    setBusquedaActiva={setBusquedaActiva}
                    clearFilterActivo={clearFilterActivo}
                    setClearFilterActivo={setClearFilterActivo}
                    mensaje={t('messages.noOffices')}
                  />
                </Box>
              </Box>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default withRouter(Oficinas);
